import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import Showdown from 'showdown';

import { environment } from '../../../../environments/environment';
import { AsistenteAccionesService } from './asistente-acciones.service';
import {
  NuevoProyectoDocDialogComponent,
  NuevoProyectoDocResult,
} from './nuevo-proyecto-doc-dialog.component';

export type Empresa = 'invering' | 'infinitus';

/**
 * Abre el asistente dedicado de una empresa en su propia ventana.
 *
 * El nombre de ventana por empresa hace que un segundo clic REUSE la ventana ya
 * abierta en vez de abrir otra. La sesión viaja sola: el JWT vive en
 * localStorage (compartido entre ventanas del mismo origen) y la ruta está
 * protegida por `authGuard`.
 */
export function abrirVentanaAsistente(empresa: Empresa): void {
  window.open(
    `/asistente/ventana/${empresa}`,
    `asistente_${empresa}`,
    'width=1100,height=800',
  );
}

/**
 * Deduce a qué empresa pertenece una ruta del CRM, para saber qué asistente
 * abrir desde la burbuja. Ante la duda, Invering (es el grueso del CRM).
 */
export function empresaDeRuta(ruta: string): Empresa {
  if (ruta.includes('/community/infinitus')) return 'infinitus';
  if (ruta.includes('/community/invering')) return 'invering';
  if (ruta.includes('/apps/infinitus')) return 'infinitus';
  // Administración (SIMI, inmuebles, arriendos, pólizas Libertador) es de
  // Infinitus; `configuracion` y `audit-log` son de Sistema, no de la empresa.
  if (ruta.includes('/apps/admin') && !ruta.includes('/configuracion')) {
    return 'infinitus';
  }
  return 'invering';
}

/** Acción que el asistente propone y el usuario confirma (Fase 2). El back la
 * arma en `accionesPropuestas`; el front la renderiza como card con Confirmar. */
export interface AccionPropuesta {
  accion: string;
  titulo: string;
  resumen: string;
  params: Record<string, unknown>;
  requiereRol: string;
  permitido: boolean;
  motivoBloqueo?: string | null;
  // estado local del card (no viene del back)
  estado?: 'pendiente' | 'ejecutando' | 'hecha' | 'cancelada' | 'error' | 'reemplazada';
  resultadoMsg?: string;
}

export interface Mensaje {
  role: 'user' | 'assistant';
  content: string;
  acciones?: AccionPropuesta[];
}

/** Una conversación de un asistente dedicado (multi-hilo). La burbuja no usa. */
export interface Conversacion {
  id: number;
  empresa: string;
  titulo: string | null;
  updatedAt: string;
}

/**
 * Configuración del hilo que consume el chat. Es lo único que cambia entre la
 * burbuja del CRM y la ventana dedicada de cada empresa.
 */
export interface HiloCtx {
  /** `null` = burbuja contextual (ve todo). `invering`/`infinitus` = asistente
   * dedicado: el back le acota alcance, herramientas e historial. */
  empresa: string | null;
  /** Ruta que se manda como contexto al backend. */
  ruta: () => string;
  /** Proyecto en foco. La burbuja lo saca de la URL; la ventana no tiene. */
  proyectoId: () => number | null;
  /** Navegar al proyecto recién creado. Sólo aplica dentro del CRM. */
  irAProyecto?: (proyectoId: number) => Promise<unknown>;
}

/**
 * Motor del chat del asistente: estado de la conversación + HTTP + acciones +
 * ingesta de documentos. Lo comparten la burbuja y las ventanas dedicadas para
 * que exista UNA sola implementación de la lógica (que ya está probada).
 *
 * Es singleton por VENTANA del navegador: la burbuja vive en la ventana del CRM
 * y cada asistente dedicado en la suya, así que no compiten por el estado.
 */
@Injectable({ providedIn: 'root' })
export class AsistenteChatService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly acciones = inject(AsistenteAccionesService);
  private readonly api = environment.apiUrl;

  mensajes: Mensaje[] = [];
  enviando = false;
  subiendoDoc = false;
  cargandoHistorial = false;
  historialCargado = false;

  // Multi-conversación (sólo ventanas dedicadas; la burbuja los ignora).
  conversaciones: Conversacion[] = [];
  conversacionActualId: number | null = null;

  private ctx: HiloCtx = {
    empresa: null,
    ruta: () => '',
    proyectoId: () => null,
  };

  configurar(ctx: HiloCtx): void {
    this.ctx = ctx;
  }

  /** Query string del hilo, para los endpoints de historial. */
  private get qsEmpresa(): string {
    return this.ctx.empresa ? `?empresa=${this.ctx.empresa}` : '';
  }

  /** Contexto que espera `POST /asistente/contextual`.
   * Ojo: ContextoModulo es un BaseModel plano (snake_case), no camelCase — por
   * eso `conversacion_id` va con guión bajo. */
  private contextoActual(): Record<string, unknown> {
    const pid = this.ctx.proyectoId();
    return {
      ruta: this.ctx.ruta(),
      extras: pid !== null ? { proyectoId: pid } : {},
      empresa: this.ctx.empresa,
      conversacion_id: this.conversacionActualId,
    };
  }

  // ── Conversaciones (multi-hilo) ──────────────────────────────────────

  /** Arranque de una ventana dedicada: refresca la lista y abre un chat nuevo
   * (vacío). La conversación se crea recién cuando el usuario manda algo. */
  inicializarVentana(): void {
    this.nuevaConversacion();
    this.refrescarConversaciones();
  }

  refrescarConversaciones(): void {
    if (!this.ctx.empresa) return;
    this.http
      .get<Conversacion[]>(
        `${this.api}/asistente/conversaciones?empresa=${this.ctx.empresa}`,
      )
      .subscribe({
        next: (list) => (this.conversaciones = list),
        error: () => undefined,
      });
  }

  /** Chat nuevo: limpia la vista y deja el hilo sin id (se crea al enviar). */
  nuevaConversacion(): void {
    this.conversacionActualId = null;
    this.mensajes = [];
  }

  esConversacionActual(c: Conversacion): boolean {
    return c.id === this.conversacionActualId;
  }

  seleccionarConversacion(c: Conversacion): void {
    if (this.conversacionActualId === c.id) return;
    this.conversacionActualId = c.id;
    this.mensajes = [];
    this.cargandoHistorial = true;
    this.http
      .get<{ mensajes: { role: 'user' | 'assistant'; content: string }[] }>(
        `${this.api}/asistente/historial?empresa=${this.ctx.empresa}&conversacion_id=${c.id}`,
      )
      .subscribe({
        next: (out) => {
          this.cargandoHistorial = false;
          this.mensajes = (out.mensajes ?? []).map((m) => ({
            role: m.role,
            content: m.content,
          }));
        },
        error: () => (this.cargandoHistorial = false),
      });
  }

  eliminarConversacion(c: Conversacion): void {
    this.http
      .delete(`${this.api}/asistente/conversaciones/${c.id}`)
      .subscribe({
        next: () => {
          this.conversaciones = this.conversaciones.filter((x) => x.id !== c.id);
          if (this.conversacionActualId === c.id) this.nuevaConversacion();
        },
        error: () => undefined,
      });
  }

  /** Garantiza que exista una conversación antes de mandar (ventanas). Para la
   * burbuja (sin empresa) ejecuta `luego` directo. */
  private asegurarConversacion(luego: () => void): void {
    if (!this.ctx.empresa || this.conversacionActualId !== null) {
      luego();
      return;
    }
    this.http
      .post<Conversacion>(`${this.api}/asistente/conversaciones`, {
        empresa: this.ctx.empresa,
      })
      .subscribe({
        next: (c) => {
          this.conversacionActualId = c.id;
          this.conversaciones = [c, ...this.conversaciones];
          luego();
        },
        error: () => {
          this.enviando = false;
          this.subiendoDoc = false;
          this.mensajes.push({
            role: 'assistant',
            content: 'No pude iniciar la conversación. Intenta de nuevo.',
          });
        },
      });
  }

  // ── Historial ────────────────────────────────────────────────────────

  cargarHistorial(): void {
    this.cargandoHistorial = true;
    this.http
      .get<{ mensajes: { role: 'user' | 'assistant'; content: string }[] }>(
        `${this.api}/asistente/historial${this.qsEmpresa}`,
      )
      .subscribe({
        next: (out) => {
          this.cargandoHistorial = false;
          this.historialCargado = true;
          if (out.mensajes?.length) {
            this.mensajes = out.mensajes.map((m) => ({
              role: m.role,
              content: m.content,
            }));
          }
        },
        error: () => {
          this.cargandoHistorial = false;
          this.historialCargado = true;
        },
      });
  }

  /** Borra el historial persistido DE ESTE HILO (no toca el de los otros
   * asistentes). `alTerminar` sirve para re-poner el saludo inicial. */
  reiniciar(alTerminar?: () => void): void {
    const limpiar = () => {
      this.mensajes = [];
      alTerminar?.();
    };
    this.http
      .delete(`${this.api}/asistente/historial${this.qsEmpresa}`)
      .subscribe({
        next: limpiar,
        // Aunque falle el DELETE limpiamos localmente para no bloquear al usuario.
        error: limpiar,
      });
  }

  // ── Conversación ─────────────────────────────────────────────────────

  enviar(texto: string): void {
    const limpio = texto.trim();
    if (!limpio || this.enviando) return;
    this.mensajes.push({ role: 'user', content: limpio });
    this.enviando = true;
    this.asegurarConversacion(() => this.postContextual(limpio));
  }

  /** Turno automático (post-extracción): no muestra burbuja de usuario. */
  private enviarAuto(texto: string): void {
    this.enviando = true;
    this.asegurarConversacion(() =>
      this.postContextual(
        texto,
        'Procesé el documento pero no pude generar el resumen. Preguntame por él.',
      ),
    );
  }

  private postContextual(mensaje: string, msgError?: string): void {
    this.http
      .post<{ content: string; accionesPropuestas?: AccionPropuesta[] }>(
        `${this.api}/asistente/contextual`,
        {
          mensaje,
          contexto: this.contextoActual(),
          historial: this.mensajes
            .slice(-10)
            .map((m) => ({ role: m.role, content: m.content })),
        },
      )
      .subscribe({
        next: (out) => {
          this.enviando = false;
          const nuevas: AccionPropuesta[] = (out.accionesPropuestas ?? []).map(
            (a) => ({ ...a, estado: 'pendiente' as const }),
          );
          // Si el usuario refinó una acción (p.ej. "asignásela a David"), el
          // modelo re-propone la MISMA acción. Marcamos las previas aún
          // pendientes como "reemplazadas" para no confirmar duplicados.
          this.superarPropuestasPrevias(nuevas);
          this.mensajes.push({
            role: 'assistant',
            content: out.content,
            acciones: nuevas,
          });
          // Ventana: el back pudo autogenerar el título y subió la conversación
          // al tope por updated_at → refrescamos la lista para reflejarlo.
          this.refrescarConversaciones();
        },
        error: (err) => {
          this.enviando = false;
          this.mensajes.push({
            role: 'assistant',
            content:
              msgError ??
              (err?.status === 503
                ? 'El asistente IA no está configurado en este entorno.'
                : 'No pude procesar tu mensaje. Inténtalo de nuevo.'),
          });
        },
      });
  }

  private superarPropuestasPrevias(nuevas: AccionPropuesta[]): void {
    const accionesNuevas = new Set(nuevas.map((a) => a.accion));
    for (const m of this.mensajes) {
      for (const a of m.acciones ?? []) {
        if ((!a.estado || a.estado === 'pendiente') && accionesNuevas.has(a.accion)) {
          a.estado = 'reemplazada';
        }
      }
    }
  }

  // ── Acciones (Fase 2) ────────────────────────────────────────────────

  /** Confirma una acción propuesta → la ejecuta de verdad en el back (que
   * re-valida el RBAC). Al terminar deja el card en estado final y agrega un
   * mensaje del asistente con el resultado. */
  confirmarAccion(a: AccionPropuesta): void {
    if (a.estado && a.estado !== 'pendiente') return;
    a.estado = 'ejecutando';
    this.http
      .post<{ ok: boolean; mensaje: string; proyecto_id?: number }>(
        `${this.api}/asistente/ejecutar-accion`,
        { accion: a.accion, params: a.params },
      )
      .subscribe({
        next: (res) => {
          a.estado = 'hecha';
          a.resultadoMsg = res.mensaje;
          this.mensajes.push({ role: 'assistant', content: `✅ ${res.mensaje}` });

          // Proyecto recién creado desde el chat: entramos a él para poder
          // adjuntarle el contrato con el 📎 sin navegar a mano.
          if (a.accion === 'crear_proyecto' && res.proyecto_id) {
            this.ctx.irAProyecto?.(res.proyecto_id);
          }
          // Avisar a las vistas abiertas (p.ej. el tab de tareas) que algo
          // cambió, para que se refresquen sin recargar la página.
          const pid =
            (res.proyecto_id as number | undefined) ??
            (a.params?.['proyecto_id'] as number | undefined) ??
            this.ctx.proyectoId();
          this.acciones.emitir({
            accion: a.accion,
            proyectoId: pid ?? null,
            resultado: res,
          });
        },
        error: (err) => {
          a.estado = 'error';
          a.resultadoMsg =
            err?.error?.detail || 'No se pudo ejecutar la acción. Intenta de nuevo.';
          this.mensajes.push({ role: 'assistant', content: `⚠️ ${a.resultadoMsg}` });
        },
      });
  }

  cancelarAccion(a: AccionPropuesta): void {
    if (a.estado && a.estado !== 'pendiente') return;
    a.estado = 'cancelada';
  }

  // ── Ingesta de documentos (Fase 3) ───────────────────────────────────

  puedeAdjuntar(): boolean {
    return !this.subiendoDoc && !this.enviando;
  }

  /** Procesa uno o varios documentos con IA (contrato, pólizas, audio,
   * planilla). Si hay un proyecto en foco se adjuntan a ese; si no, se pide lo
   * mínimo (nombre + empresa) y se crea el proyecto y se suben todos en una
   * sola operación. Siempre se dispara UNA extracción sobre el lote. */
  adjuntarArchivos(files: File[]): void {
    if (files.length === 0) return;

    const pid = this.ctx.proyectoId();
    if (pid !== null) {
      this.subirDocsAProyecto(pid, files);
      return;
    }

    // Sin proyecto en foco: el backend necesita nombre + empresa (no se pueden
    // deducir de los archivos). Los pedimos una vez y hacemos todo de una.
    this.dialog
      .open(NuevoProyectoDocDialogComponent, {
        data: {
          fileNames: files.map((f) => f.name),
          nombreSugerido: this.sugerirNombreProyecto(files),
          // En un asistente dedicado la empresa ya está decidida.
          empresaFija: this.ctx.empresa,
        },
        width: '460px',
        maxWidth: '95vw',
      })
      .afterClosed()
      .subscribe((res: NuevoProyectoDocResult | undefined) => {
        if (res) this.crearProyectoYSubir(res, files);
      });
  }

  /** Sugiere el nombre del proyecto: preferimos el PDF (suele ser el contrato). */
  private sugerirNombreProyecto(files: File[]): string {
    const base = files.find((f) => f.name.toLowerCase().endsWith('.pdf')) ?? files[0];
    return base.name.replace(/\.[^.]+$/, '');
  }

  private subirDocsAProyecto(proyectoId: number, files: File[]): void {
    this.iniciarSubida(files);
    this.http
      .post<{ reunionId: number }>(
        `${this.api}/asistente/proyectos/${proyectoId}/adjuntar-doc`,
        this.armarFormData(files),
      )
      .subscribe({
        next: (out) => this.pollExtraccion(out.reunionId, 0),
        error: (err) => this.fallaSubida(err?.error?.detail),
      });
  }

  private crearProyectoYSubir(datos: NuevoProyectoDocResult, files: File[]): void {
    this.iniciarSubida(files);
    const fd = this.armarFormData(files);
    fd.append('nombre', datos.nombre);
    fd.append('empresa', datos.empresa);
    this.http
      .post<{ proyectoId: number; reunionId: number; codigo: string }>(
        `${this.api}/asistente/adjuntar-doc`,
        fd,
      )
      .subscribe({
        next: (out) => {
          this.mensajes.push({
            role: 'assistant',
            content: `Creé el proyecto **${out.codigo}** «${datos.nombre}». La IA está leyendo los documentos…`,
          });
          const seguir = () => this.pollExtraccion(out.reunionId, 0);
          const nav = this.ctx.irAProyecto?.(out.proyectoId);
          if (nav) {
            nav.then(seguir);
          } else {
            seguir();
          }
        },
        error: (err) => this.fallaSubida(err?.error?.detail),
      });
  }

  /** El backend espera el campo repetido `files` (list[UploadFile]). */
  private armarFormData(files: File[]): FormData {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    return fd;
  }

  private iniciarSubida(files: File[]): void {
    this.subiendoDoc = true;
    const nombres = files.map((f) => f.name).join(', ');
    this.mensajes.push({
      role: 'user',
      content:
        files.length === 1 ? `📎 ${nombres}` : `📎 ${files.length} archivos: ${nombres}`,
    });
    this.mensajes.push({
      role: 'assistant',
      content:
        files.length === 1
          ? `Subiendo y procesando **${nombres}** con IA… esto puede tardar 1–2 minutos.`
          : `Subiendo y procesando **${files.length} documentos** con IA… esto puede tardar 1–2 minutos.`,
    });
  }

  private fallaSubida(detalle?: string): void {
    this.subiendoDoc = false;
    this.mensajes.push({
      role: 'assistant',
      content: `⚠️ ${detalle || 'No pude subir o procesar el documento. Intenta de nuevo.'}`,
    });
  }

  /** Pollea el estado_ia de la reunión hasta que la extracción termine. */
  private pollExtraccion(reunionId: number, intentos: number): void {
    const MAX = 45; // ~3 min a 4s
    this.http.get<{ estadoIa: string }>(`${this.api}/reuniones/${reunionId}`).subscribe({
      next: (rn) => {
        if (rn.estadoIa === 'completado') {
          this.subiendoDoc = false;
          this.enviarAuto(
            `El documento del proyecto ya se procesó (reunión ${reunionId}). ` +
              `Resumime brevemente qué se extrajo y, si la extracción está completa, ` +
              `llama la acción aplicar_extraccion con reunion_id=${reunionId} para dejarme el botón de aplicar.`,
          );
        } else if (rn.estadoIa === 'error') {
          this.subiendoDoc = false;
          this.mensajes.push({
            role: 'assistant',
            content: '⚠️ La extracción falló. Puedes reintentar subiendo el documento otra vez.',
          });
        } else if (intentos < MAX) {
          setTimeout(() => this.pollExtraccion(reunionId, intentos + 1), 4000);
        } else {
          this.subiendoDoc = false;
          this.mensajes.push({
            role: 'assistant',
            content:
              'La extracción está tardando más de lo normal. Preguntame en un rato por el resumen del documento.',
          });
        }
      },
      error: () => {
        if (intentos < MAX) {
          setTimeout(() => this.pollExtraccion(reunionId, intentos + 1), 4000);
        } else {
          this.subiendoDoc = false;
          this.mensajes.push({
            role: 'assistant',
            content:
              '⚠️ No pude verificar el estado de la extracción. Preguntame en un rato por el resumen del documento.',
          });
        }
      },
    });
  }

  // ── Render ───────────────────────────────────────────────────────────

  /**
   * Render markdown COMPLETO (tablas, encabezados, listas, citas, código).
   * Lo usa la ventana dedicada, donde hay ancho para que se vea bien; la
   * burbuja se queda con el `formatear()` liviano porque es angosta.
   *
   * Angular sanitiza el resultado al pasarlo por `[innerHTML]`, así que el
   * HTML peligroso (scripts, handlers) se descarta solo.
   */
  private readonly md = new Showdown.Converter({
    tables: true,
    strikethrough: true,
    ghCodeBlocks: true,
    tasklists: true,
    // El modelo separa párrafos con un solo \n muchas veces; sin esto se
    // pegarían las líneas.
    simpleLineBreaks: true,
    simplifiedAutoLink: true,
    openLinksInNewWindow: true,
    headerLevelStart: 3, // que un "#" del modelo no compita con el H1 de la vista
  });
  private readonly mdCache = new Map<string, string>();

  formatearMarkdown(texto: string): string {
    const cacheado = this.mdCache.get(texto);
    if (cacheado !== undefined) return cacheado;
    const html = this.md.makeHtml(texto);
    this.mdCache.set(texto, html);
    return html;
  }

  // formatear() se invoca desde [innerHTML] en cada ciclo de CD: memoizamos por
  // contenido para no re-correr la cadena de regex en cada tick.
  private readonly formatCache = new Map<string, string>();

  formatear(texto: string): string {
    const cacheado = this.formatCache.get(texto);
    if (cacheado !== undefined) return cacheado;
    const html = texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
      .replace(/\n/g, '<br>');
    this.formatCache.set(texto, html);
    return html;
  }
}
